import ReactImageUploading, {
  ImageListType,
  ImageType,
} from 'react-images-uploading';
import { RotateLeft, RotateRight } from '@material-ui/icons';
import { IdProvider } from '~/common/api/common/apiCommons';
import { has, hasNot } from '~/common/utils/objectUtils';
import { DragAndDropList } from './DragAndDropList';
import { Box, Icon, Stack } from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { Button } from './Button';
import { Image } from './Image';
import { useToast } from '.';
import axios from 'axios';
import React from 'react';
import _ from 'lodash';

interface ImageListProps {
  initialImages: ImageListType;
  onChange?: React.Dispatch<React.SetStateAction<ImageListType>>;
  onRotate?: (
    vars: IdProvider & { rotation: 0 | 90 | 180 | 270 },
  ) => Promise<string | null>;
}

export const ImageList: React.FC<ImageListProps> = ({
  initialImages,
  onChange,
  onRotate,
}) => {
  const [images, setImages] = React.useState<ImageListType>([]);

  React.useEffect(() => {
    if (!!initialImages && !images?.length) {
      setImages(initialImages);
    }
  }, [initialImages]);

  React.useEffect(() => {
    if (onChange) {
      onChange(images ?? []);
    }
  }, [images, onChange]);

  const toast = useToast({ title: 'rotate.picture.error', status: 'error' });
  const onError = () => toast({});

  return (
    <ReactImageUploading
      value={images}
      onChange={setImages}
      multiple
      acceptType={['jpg', 'jpeg']}
    >
      {({ imageList, onImageUpload, onImageRemove, dragProps, isDragging }) => (
        <Stack h="full" justify="space-between">
          <Box overflowY="scroll" h="full">
            <DragAndDropList
              ids={imageList.map((img) => img.dataURL ?? '')}
              onDropEnd={(result) => {
                const destination = result.destination?.index;
                const source = result.source.index;

                if (!_.isNil(destination) && destination !== source) {
                  setImages((images) => {
                    const [moved] = images.splice(source, 1);

                    images.splice(
                      destination > source ? destination - 1 : destination,
                      0,
                      moved,
                    );

                    return [...images];
                  });
                }
              }}
              listElements={imageList.map((image, index) => {
                return (
                  <Box
                    key={index}
                    position="relative"
                    w="full"
                    p={1}
                    border="gray"
                    shadow="sm"
                    borderRadius="base"
                    overflow="hidden"
                  >
                    <Image
                      src={image.dataURL}
                      alt=""
                      top={0}
                      left={0}
                      right={0}
                      w="full"
                    />
                    <Box
                      position="absolute"
                      top={2}
                      right={3}
                      onClick={() => onImageRemove(index)}
                      _hover={{ cursor: 'pointer' }}
                      zIndex={2}
                    >
                      <CloseIcon color="red.600" />
                    </Box>
                    <Box
                      position="absolute"
                      top={2}
                      right={8}
                      _hover={{ cursor: 'pointer' }}
                      zIndex={2}
                      p={1}
                      onClick={() => {
                        image.id && onRotate
                          ? onRotate({
                              id: image.id as number,
                              rotation: 270,
                            })
                              .then((newImg) => {
                                const imgIndex = images.findIndex(
                                  (i) => i.id === image.id,
                                );
                                if (imgIndex !== -1) {
                                  setImages((images) => {
                                    images[imgIndex] = {
                                      ...images[imgIndex],
                                      dataURL: newImg ?? undefined,
                                    };
                                    return [...images];
                                  });
                                }
                              })
                              .catch(onError)
                          : axios
                              .post<string>('/images/rotate/any/', {
                                image: image.dataURL,
                                rotation: 270,
                              })
                              .then((res) => {
                                const newImg: string = res.data;
                                setImages((images) => {
                                  images[index].dataURL = newImg;
                                  return [...images];
                                });
                              })
                              .catch(onError);
                      }}
                    >
                      <Icon as={RotateRight} />
                    </Box>
                    <Box
                      position="absolute"
                      top={2}
                      right={16}
                      _hover={{ cursor: 'pointer' }}
                      zIndex={2}
                      p={1}
                      onClick={() => {
                        image.id && onRotate
                          ? onRotate({ id: image.id as number, rotation: 90 })
                              .then((newImg) => {
                                const imgIndex = images.findIndex(
                                  (i) => i.id === image.id,
                                );
                                if (imgIndex !== -1) {
                                  setImages((images) => {
                                    images[imgIndex] = {
                                      ...images[imgIndex],
                                      dataURL: newImg ?? undefined,
                                    };
                                    return [...images];
                                  });
                                }
                              })
                              .catch(onError)
                          : axios
                              .post<string>('/images/rotate/any/', {
                                image: image.dataURL,
                                rotation: 90,
                              })
                              .then((res) => {
                                const newImg: string = res.data;
                                setImages((images) => {
                                  images[index].dataURL = newImg;
                                  return [...images];
                                });
                              })
                              .catch(onError);
                      }}
                    >
                      <Icon as={RotateLeft} />
                    </Box>
                  </Box>
                );
              })}
            />
          </Box>
          <Button
            onClick={onImageUpload}
            variant={isDragging ? 'submit' : 'solid'}
            w="full"
            {...dragProps}
          >
            {isDragging
              ? 'createEditJob.addPic.draggingStyle'
              : 'createEditJob.addPic'}
          </Button>
        </Stack>
      )}
    </ReactImageUploading>
  );
};

export const useImageListState = () => {
  const [initialImages, setInitialImages] = React.useState<ImageListType>([]);
  const [images, setImages] = React.useState<ImageListType>([]);

  const { added, removed } = React.useMemo(() => {
    const removed = (_.differenceBy(
      initialImages,
      _.filter(images, has('id')),
      'id',
    ) ?? []) as (ImageType & { id: number })[];
    const added = _.filter(images, hasNot('id')) ?? [];
    return { added, removed };
  }, [images, initialImages]);

  return {
    initialImages,
    setInitialImages,
    setImages,
    addedImages: added,
    removedImages: removed,
    images,
  };
};
